import { getBaseUrl } from './getBaseUrl';

const isFullUrl = (value: string) =>
  /^[a-z][a-z0-9+.-]*:/i.test(value) || value.startsWith('//');

const normalizeLegacyUploadPath = (value: string) => {
  let path = value.trim();
  if (!path) return '';

  path = path.replace(/^\/+/, '');
  if (path.startsWith('file/uploads/')) {
    path = path.slice('file/'.length);
  }

  if (!path.startsWith('uploads/')) {
    return value.trim();
  }

  return `/${path}`;
};

const isMediaObjectKey = (value: string) =>
  /^(attendance|common|patrol|users|workers)\//.test(value);

const normalizeHostname = (hostname: string) =>
  hostname
    .trim()
    .toLowerCase()
    .replace(/^\[|\]$/g, '');

const isPrivateUploadHost = (hostname: string) => {
  const host = normalizeHostname(hostname);
  return (
    host === 'localhost' ||
    host === '0.0.0.0' ||
    host === '::1' ||
    host.startsWith('127.') ||
    host.startsWith('10.') ||
    host.startsWith('192.168.') ||
    host.startsWith('169.254.') ||
    /^172\.(1[6-9]|2\d|3[01])\./.test(host)
  );
};

const normalizePrivateLegacyUploadUrl = (value: string) => {
  try {
    const url = new URL(value.startsWith('//') ? `https:${value}` : value);
    if (!isPrivateUploadHost(url.hostname)) return '';

    const path = normalizeLegacyUploadPath(url.pathname);
    if (!path.startsWith('/uploads/')) return '';
    return `${path}${url.search}${url.hash}`;
  } catch (_error) {
    return '';
  }
};

const joinBaseAndPath = (pathValue: string, baseURL: string) => {
  const base = `${baseURL || ''}`.trim().replace(/\/$/, '');
  const path = pathValue.startsWith('/') ? pathValue : `/${pathValue}`;
  if (!base) return path;
  return `${base}${path}`;
};

export const resolveMediaUrl = (
  value?: string | null,
  baseURL = getBaseUrl(),
) => {
  const raw = `${value || ''}`.trim();
  if (!raw) return '';
  const privateUploadPath = normalizePrivateLegacyUploadUrl(raw);
  if (privateUploadPath) return joinBaseAndPath(privateUploadPath, baseURL);
  if (isFullUrl(raw)) return raw;

  const normalizedPath = normalizeLegacyUploadPath(raw);
  if (
    !normalizedPath.startsWith('/uploads/') &&
    !isMediaObjectKey(normalizedPath)
  ) {
    return normalizedPath;
  }

  return joinBaseAndPath(normalizedPath, baseURL);
};

export const normalizeMediaList = (
  value?: string[] | string | null,
  baseURL = getBaseUrl(),
  separator = ',',
) => {
  const items = Array.isArray(value)
    ? value
    : `${value || ''}`.split(separator);
  return items.map((item) => resolveMediaUrl(item, baseURL)).filter(Boolean);
};
